import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, PencilLine, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  QUICK_ADD_CATEGORY_OPTIONS,
  QUICK_ADD_WORKSPACE_OPTIONS,
} from './commandCenterSuggestions';

const EMPTY_FORM = {
  title: '',
  detail: '',
  workspace: 'communication',
  category: 'people',
  dueAt: '',
  targetRoomId: '',
  targetTab: '',
  targetAnchor: '',
  targetRecordId: '',
  highlightMode: '',
};

function toDateTimeLocalValue(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function buildInitialFormState(item) {
  return {
    title: item?.title || '',
    detail: item?.detail || '',
    workspace: item?.workspace || 'communication',
    category: item?.category || 'people',
    dueAt: toDateTimeLocalValue(item?.due_at),
    targetRoomId: item?.target_room_id || '',
    targetTab: item?.target_tab || '',
    targetAnchor: item?.target_anchor || '',
    targetRecordId: item?.target_record_id || '',
    highlightMode: item?.highlight_mode || '',
  };
}

function buildSubmitPayload(formState) {
  return {
    title: formState.title.trim(),
    detail: formState.detail.trim(),
    workspace: formState.workspace,
    category: formState.category,
    due_at: formState.dueAt ? new Date(formState.dueAt).toISOString() : null,
    target_room_id: formState.targetRoomId.trim() || null,
    target_tab: formState.targetTab.trim() || null,
    target_anchor: formState.targetAnchor.trim() || null,
    target_record_id: formState.targetRecordId.trim() || null,
    highlight_mode: formState.highlightMode.trim() || null,
  };
}

export default function QueueItemEditDialog({
  item,
  open,
  saving,
  onOpenChange,
  onSubmit,
  onDelete,
  onResolve,
  onDefer,
  onPromoteToActive,
  onSendToLater,
  onDismiss,
}) {
  const [formState, setFormState] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open || !item) {
      setFormState(EMPTY_FORM);
      return;
    }
    setFormState(buildInitialFormState(item));
  }, [item, open]);

  const canSave = Boolean(formState.title.trim());
  const dialogTitle = useMemo(() => {
    if (item?.source_type === 'slack') return 'Edit Slack Capture';
    return 'Edit Queue Item';
  }, [item?.source_type]);

  const updateField = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    if (!item?.queue_item_id || !canSave || saving) return;
    const result = await onSubmit?.(item.queue_item_id, buildSubmitPayload(formState));
    if (result) {
      onOpenChange(false);
    }
  };

  const handleDelete = async () => {
    if (!item?.queue_item_id || saving || !window.confirm('Are you sure you want to delete this queue item entirely?')) return;
    const result = await onDelete?.(item.queue_item_id);
    if (result) {
      onOpenChange(false);
    }
  };

  const handleResolve = async () => {
    if (!item?.queue_item_id || saving) return;
    const result = await onResolve?.(item.queue_item_id);
    if (result) {
      onOpenChange(false);
    }
  };

  const handleDefer = async () => {
    if (!item?.queue_item_id || saving) return;
    const result = await onDefer?.(item.queue_item_id);
    if (result) {
      onOpenChange(false);
    }
  };

  const handlePromoteToActive = async () => {
    if (!item?.queue_item_id || saving) return;
    const result = await onPromoteToActive?.(item.queue_item_id);
    if (result) {
      onOpenChange(false);
    }
  };

  const handleSendToLater = async () => {
    if (!item?.queue_item_id || saving) return;
    const result = await onSendToLater?.(item.queue_item_id);
    if (result) {
      onOpenChange(false);
    }
  };

  const handleDismiss = async () => {
    if (!item?.queue_item_id || saving || !window.confirm('Remove this needs-review item from the queue?')) return;
    const result = await onDismiss?.(item.queue_item_id);
    if (result) {
      onOpenChange(false);
    }
  };

  const renderStateActions = () => {
    if (!item?.state) return null;

    if (item.state === 'active') {
      return (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={handleResolve} disabled={saving}>
            Mark complete
          </Button>
          <Button type="button" variant="outline" onClick={handleDefer} disabled={saving}>
            Move to Later
          </Button>
        </div>
      );
    }

    if (item.state === 'later') {
      return (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={handlePromoteToActive} disabled={saving}>
            Bring back to Active
          </Button>
          <Button type="button" variant="outline" onClick={handleResolve} disabled={saving}>
            Mark complete
          </Button>
        </div>
      );
    }

    if (item.state === 'needs_review') {
      return (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={handlePromoteToActive} disabled={saving}>
            Promote to Active
          </Button>
          <Button type="button" variant="outline" onClick={handleSendToLater} disabled={saving}>
            Send to Later
          </Button>
          <Button type="button" variant="outline" onClick={handleDismiss} disabled={saving}>
            Dismiss
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>
            Tighten the title, due date, workspace, and exact destination before this item drives real work.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5">
          <div className="rounded-2xl border border-sky-200 bg-sky-50/80 p-4 dark:border-sky-400/15 dark:bg-sky-500/5">
            <div className="flex items-start gap-3">
              <div className="rounded-full border border-sky-200 bg-white p-2 dark:border-sky-400/20 dark:bg-black/20">
                <PencilLine className="h-4 w-4 text-sky-700 dark:text-sky-200" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-sky-700 dark:text-sky-200">
                  Queue Triage
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
                  Add the exact destination only when you know it. Leave destination fields blank if the item still needs human judgment first.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-medium text-slate-900 dark:text-white">Title</label>
            <Input
              value={formState.title}
              onChange={(event) => updateField('title', event.target.value)}
              placeholder="What should this action be called?"
              autoFocus
            />
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-medium text-slate-900 dark:text-white">Detail</label>
            <Textarea
              value={formState.detail}
              onChange={(event) => updateField('detail', event.target.value)}
              placeholder="More context for the operator."
              rows={5}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="grid gap-2">
              <label className="text-sm font-medium text-slate-900 dark:text-white">Workspace</label>
              <Select value={formState.workspace} onValueChange={(value) => updateField('workspace', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose workspace" />
                </SelectTrigger>
                <SelectContent>
                  {QUICK_ADD_WORKSPACE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium text-slate-900 dark:text-white">Category</label>
              <Select value={formState.category} onValueChange={(value) => updateField('category', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose category" />
                </SelectTrigger>
                <SelectContent>
                  {QUICK_ADD_CATEGORY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium text-slate-900 dark:text-white">Due date</label>
              <Input
                type="datetime-local"
                value={formState.dueAt}
                onChange={(event) => updateField('dueAt', event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-white/10 dark:bg-white/[0.03] md:grid-cols-2">
            <div className="grid gap-2">
              <label className="text-sm font-medium text-slate-900 dark:text-white">Target room id</label>
              <Input value={formState.targetRoomId} onChange={(event) => updateField('targetRoomId', event.target.value)} placeholder="guide-partners" />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium text-slate-900 dark:text-white">Target tab</label>
              <Input value={formState.targetTab} onChange={(event) => updateField('targetTab', event.target.value)} placeholder="guestguides" />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium text-slate-900 dark:text-white">Target anchor</label>
              <Input value={formState.targetAnchor} onChange={(event) => updateField('targetAnchor', event.target.value)} placeholder="guide-partner-outreach" />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium text-slate-900 dark:text-white">Target record id</label>
              <Input value={formState.targetRecordId} onChange={(event) => updateField('targetRecordId', event.target.value)} placeholder="optional record id" />
            </div>

            <div className="grid gap-2 md:col-span-2">
              <label className="text-sm font-medium text-slate-900 dark:text-white">Highlight mode</label>
              <Input value={formState.highlightMode} onChange={(event) => updateField('highlightMode', event.target.value)} placeholder="optional highlight mode" />
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col items-stretch gap-3 sm:flex-col">
          {renderStateActions() ? (
            <div className="flex items-center justify-start">
              {renderStateActions()}
            </div>
          ) : null}

          <div className="flex items-center justify-between sm:justify-between">
          <Button type="button" variant="ghost" onClick={handleDelete} disabled={saving} className="text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/10">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={!canSave || saving} className="bg-aqua text-charcoal hover:bg-aqua-dark">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save changes
            </Button>
          </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
